/**
 * Returns a user's saved column order with every column it does not know
 * about slotted in next to its neighbour from the column definitions, instead
 * of react-table's default of appending unknown columns at the end of the
 * table.
 *
 * A column added to the app after a user saved their order (e.g. "Label
 * generated by", defined right after "Label Generation Date") should appear
 * where the definitions put it, not as the last column.
 *
 * - Saved ids that no longer exist are dropped, as are duplicates.
 * - Each missing id is inserted after the closest column that precedes it in
 *   `definitionOrder` and is already in the result; one with no such
 *   predecessor goes first. Missing ids are processed in definition order, so
 *   consecutive new columns keep their relative order.
 * - With nothing saved, the result is the definition order.
 */
export const mergeColumnOrder = (
  savedOrder: string[],
  definitionOrder: string[]
): string[] => {
  const order = savedOrder.filter(
    (id, index) =>
      definitionOrder.includes(id) && savedOrder.indexOf(id) === index
  );

  definitionOrder.forEach((id, definitionIndex) => {
    if (order.includes(id)) return;

    let insertAt = 0;
    for (let i = definitionIndex - 1; i >= 0; i--) {
      const neighbourAt = order.indexOf(definitionOrder[i]);
      if (neighbourAt !== -1) {
        insertAt = neighbourAt + 1;
        break;
      }
    }
    order.splice(insertAt, 0, id);
  });

  return order;
};
